import {
  Component, ElementRef, OnInit, Input,
  ViewChildren, ContentChildren, TemplateRef, ViewContainerRef
} from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import { CfCoreComponent } from '../core/core.component';
import { DialogModel } from '../../models/dialog/dialog.model';
import { DialogStylingModel } from '../../models/dialog/dialog-styling.model';
import { DialogTemplates } from '../../templates/dialog.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Component to show templates in a <a href="https://material.angular.io/components/dialog/overview" target="_blank" class="links">
 * Angular Material Dialog</a> with extensions for a lot of callbacks and templates for toolbar, content and footer sections.
 * It is working together with DialogService methods.</p>
 */
export class CfDialogComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden
 */
cfDialogViewContainer: ViewContainerRef;
/**
 * Dialog object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  targetId: this.id,
 *  sourceEvent: null,
 *  disableClose: false,
 *  height: "400px",
 *  width: "600px",
 *  header: {{'{'}}
 *    show: true,
 *    toolbar: {{'{'}}
 *      type: "info",
 *      info: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          "name": "info"
 *        {{'}'}}
 *      {{'}'}},
 *      content: {{'{'}}
 *        show: true,
 *        title: "My dialog title",
 *        template: this.myDialogTitleTemplate
 *      {{'}'}},
 *      help: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          name: "help_outline"
 *        {{'}'}}
 *      {{'}'}},
 *      close: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          name: "highlight_off"
 *        {{'}'}}
 *      {{'}'}}
 *    {{'}'}}
 *  {{'}'}},
 *  content: {{'{'}}
 *    show: true,
 *    message: "My dialog message",
 *    template: this.myDialogMessageTemplate
 *  {{'}'}},
 *  footer: {{'{'}}
 *    show: true,
 *    cancelButton: {{'{'}}
 *      show: true,
 *      button: {{'{'}}
 *        label: "Cancel"
 *      {{'}'}}
 *    {{'}'}},
 *    okButton: {{'{'}}
 *      show: true,
 *      button: {{'{'}}
 *        label: "Ok"
 *      {{'}'}}
 *    {{'}'}},
 *    template: this.myDialogFooterTemplate
 *  {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>targetId: string</h4>
 * ID of the target component inside which dialog will be opened.
 * <h4>sourceEvent: any</h4>
 * If to align dialog position according to the trigger which opened dialog.
 * <h4>position: string</h4>
 * Position of the dialog according to the trigger. Can be: auto, leftUp, leftBelow, rightBelow, rightUp.
 * <h4>disableClose: boolean</h4>
 * Whether the user can use escape or clicking outside to close a modal.
 * <h4>height: string</h4>
 * Height of the dialog.
 * <h4>width: string</h4>
 * Width of the dialog.
 * <h4>header: object</h4>
 * Dialog header object.
 * <h4>header.show: boolean</h4>
 * Whether to show dialog header section.
 * <h4>header.toolbar: object</h4>
 * Dialog header toolbar object.
 * <h4>content: object</h4>
 * Dialog content object.
 * <h4>content.show: boolean</h4>
 * Whether to show dialog content section.
 * <h4>content.message: string</h4>
 * Dialog message text.
 * <h4>content.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Template for the dialog content template section.
 * <h4>footer: object</h4>
 * Dialog footer object.
 * <h4>footer.show: boolean</h4>
 * Whether to show footer.
 * <h4>footer.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Template for the dialog footer template section.
 * <h4>footer.okButton: object</h4>
 * Footer ok button object.
 * <h4>footer.okButton.show: boolean</h4>
 * Whether to show footer ok button.
 * <h4>footer.okButton.button: ButtonModel</h4>
 * Ok button ButtonModel.
 * <h4>footer.cancelButton: object</h4>
 * Footer cancel button object.
 * <h4>footer.cancelButton.show: boolean</h4>
 * Whether to show footer cancel button.
 * <h4>footer.cancelButton.button: ButtonModel</h4>
 * Cancel button ButtonModel.
 */
public properties: DialogModel;
/**
 * Object for all styling properties:
 * <code><pre>
 * {{'{'}}
 *  header?: {{'{'}}                                 // Styling for the dialog header.
 *    class?: string;                          // Classes for header section.
 *    dynamicClass?: any;                      // Dynamic classes for header section.
 *    toolbar?: ToolbarStylingModel;           // ToolbarStylingModel for the header toolbar.
 *  {{'}'}};
 *  content?: {{'{'}}                                // Styling for the dialog content section with message and template inside.
 *    class?: string;                          // Classes for content section.
 *    dynamicClass?: any;                      // Dynamic classes for content section.
 *    message?: StylingModel;                  // StylingModel for the dialog content message.
 *    template?: StylingModel;                 // StylingModel for the dialog content template.
 *  {{'}'}};
 *  footer?: {{'{'}}                                 // Styling for the dialog footer.
 *    class?: string;                          // Classes for footer section.
 *    dynamicClass?: any;                      // Dynamic classes for footer section.
 *    okButton?: ButtonStylingModel;           // ButtonStylingModel for the dialog okButton.
 *    cancelButton?: ButtonStylingModel;       // ButtonStylingModel for the dialog cancelButton.
 *    template?: StylingModel;                 // StylingModel for the dialog template.
 *  {{'}'}};
 * {{'}'}}
 * </pre></code>
 */
public styling: DialogStylingModel;
/**
 * @hidden
 */
cfDialogTemplates;
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} dialogRef
 */
constructor(
public elementRef: ElementRef,
    /**@hidden */ templateService: TemplateService,
public dialogRef: MdDialogRef<any>) {
    super(elementRef, templateService);
    this.properties = new DialogModel();
    this.styling = new DialogStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void {
    if (this.properties && this.properties.compTemplate) { this.compTemplate = this.properties.compTemplate; }
    this.getMyTemplate('dialog', DialogTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'DIALOG').then( sampler => {
        // console.log('DIALOG', this);
      });
    });
  }
/**
 * @hidden 
 * Function to return material name of toolbar type
 * @param {?} name   name of toolbar type: info/warning/error
 * @return {?}
 */
getToolbarType(name) {
    return name === 'error' ? 'mat-warn' : name === 'warning' ? 'mat-accent' : 'mat-primary';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-dialog',
  template: "<div *ngIf=\"_inited && properties?.display\" attr.cf-dialog-style=\"{{properties?.sourceEvent ? 'floating' : 'normal'}}\" class=\"{{properties?.header?.toolbar?.type}} {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-container'\"> <div *ngIf=\"properties?.header?.show\" md-dialog-title class=\"{{activeTemplate.style?.header?.class}} {{styling?.header?.class}} {{getDynamicClasses(activeTemplate.style?.header?.dynamicClass)}} {{getDynamicClasses(styling?.header?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-header'\"> <cf-toolbar [properties]=\"properties?.header?.toolbar\" [styling]=\"styling?.header?.toolbar\"  (onInfo)=\"dialogRef?.close({ callback: 'infoCallback', data: $event })\" (onHelp)=\"dialogRef?.close({ callback: 'helpCallback', data: $event })\" (onClose)=\"dialogRef?.close({ callback: 'closeCallback', data: $event })\" (onMaximizeToggle)=\"dialogRef?.close({ callback: 'maximizeToggleCallback', data: $event })\" (onExpandToggle)=\"dialogRef?.close({ callback: 'expandToggleCallback', data: $event })\" (onItemClick)=\"dialogRef?.close({ callback: 'itemClickCallback', data: $event })\"></cf-toolbar> </div> <div *ngIf=\"properties?.content?.show\" md-dialog-content class=\"{{activeTemplate.style?.content?.class}} {{styling?.content?.class}} {{getDynamicClasses(activeTemplate.style?.content?.dynamicClass)}} {{getDynamicClasses(styling?.content?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-content'\"> <div class=\"{{activeTemplate.style?.content?.message?.class}} {{styling?.content?.message?.class}} {{getDynamicClasses(activeTemplate.style?.content?.message?.dynamicClass)}} {{getDynamicClasses(styling?.content?.message?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-message'\" [innerHtml]=\"properties?.content?.message\"></div> <div class=\"{{activeTemplate.style?.content?.template?.class}} {{styling?.content?.template?.class}} {{getDynamicClasses(activeTemplate.style?.content?.template?.dynamicClass)}} {{getDynamicClasses(styling?.content?.template?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-content-template'\"> <span [ngTemplateOutlet]=\"properties?.content?.template\"></span> </div> </div> <div *ngIf=\"properties?.footer?.show\" md-dialog-actions class=\"{{activeTemplate.style?.footer?.class}} {{styling?.footer?.class}} {{getDynamicClasses(activeTemplate.style?.footer?.dynamicClass)}} {{getDynamicClasses(styling?.footer?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-footer'\"> <div class=\"{{activeTemplate.style?.footer?.template?.class}} {{styling?.footer?.template?.class}} {{getDynamicClasses(activeTemplate.style?.footer?.template?.dynamicClass)}} {{getDynamicClasses(styling?.footer?.template?.dynamicClass)}}\" [ngClass]=\"'cf-dialog-footer-template'\"> <span [ngTemplateOutlet]=\"properties?.footer?.template\"></span> </div> <cf-button *ngIf=\"properties?.footer?.okButton?.show\" [properties]=\"properties?.footer?.okButton?.button\" [styling]=\"styling?.footer?.okButton\" (onClick)=\"dialogRef?.close({ callback: 'okCallback' })\"></cf-button> <cf-button *ngIf=\"properties?.footer?.cancelButton?.show\" [properties]=\"properties?.footer?.cancelButton?.button\" [styling]=\"styling?.footer?.cancelButton\" (onClick)=\"dialogRef?.close({ callback: 'cancelCallback' })\"></cf-button> </div> </div>",
  styles: ["[hidden] { display: none !important; } /deep/ .mat-dialog-container { padding: 0; } :host /deep/ .cf-dialog-header.mat-dialog-title { margin: 0; } :host /deep/ .cf-dialog-toolbar { padding: 0 24px; } :host /deep/ .cf-dialog-toolbar .mat-toolbar-row { height: auto; min-height: 64px; } :host /deep/ .cf-dialog-toolbar .mat-toolbar-row > cf-icon md-icon { color: inherit; } :host /deep/ .cf-dialog-toolbar .cf-dialog-toolbar-content { flex-grow: 1; margin: 0 8px; } :host /deep/ .cf-dialog-toolbar .cf-dialog-toolbar-content + cf-icon { margin-right: 8px; } :host /deep/ .cf-dialog-content { padding: 24px; margin: 0; } :host /deep/ .cf-dialog-footer { padding: 8px 24px; align-items: center; justify-content: flex-end; } :host /deep/ .cf-dialog-footer > cf-button { margin-left: 8px; } :host /deep/ .mat-dialog-actions:last-child { margin-bottom: 0; } .cf-calendar-dialog-content { left: 25px; } .cf-calendar-full-width { width: 100%; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: MdDialogRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'cfDialogViewContainer': [{ type: ViewChildren, args: ['cfDialogViewContainer', { read: ViewContainerRef }, ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'cfDialogTemplates': [{ type: ContentChildren, args: [TemplateRef, ] },],
};
}

function CfDialogComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfDialogComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfDialogComponent.ctorParameters;
/** @type {?} */
CfDialogComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfDialogComponent.prototype.cfDialogViewContainer;
/**
 * Dialog object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  targetId: this.id,
 *  sourceEvent: null,
 *  disableClose: false,
 *  height: "400px",
 *  width: "600px",
 *  header: {{'{'}}
 *    show: true,
 *    toolbar: {{'{'}}
 *      type: "info",
 *      info: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          "name": "info"
 *        {{'}'}}
 *      {{'}'}},
 *      content: {{'{'}}
 *        show: true,
 *        title: "My dialog title",
 *        template: this.myDialogTitleTemplate
 *      {{'}'}},
 *      help: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          name: "help_outline"
 *        {{'}'}}
 *      {{'}'}},
 *      close: {{'{'}}
 *        show: true,
 *        icon: {{'{'}}
 *          name: "highlight_off"
 *        {{'}'}}
 *      {{'}'}}
 *    {{'}'}}
 *  {{'}'}},
 *  content: {{'{'}}
 *    show: true,
 *    message: "My dialog message",
 *    template: this.myDialogMessageTemplate
 *  {{'}'}},
 *  footer: {{'{'}}
 *    show: true,
 *    cancelButton: {{'{'}}
 *      show: true,
 *      button: {{'{'}}
 *        label: "Cancel"
 *      {{'}'}}
 *    {{'}'}},
 *    okButton: {{'{'}}
 *      show: true,
 *      button: {{'{'}}
 *        label: "Ok"
 *      {{'}'}}
 *    {{'}'}},
 *    template: this.myDialogFooterTemplate
 *  {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>List of all model properties</h4>
 * <h4>targetId: string</h4>
 * ID of the target component inside which dialog will be opened.
 * <h4>sourceEvent: any</h4>
 * If to align dialog position according to the trigger which opened dialog.
 * <h4>position: string</h4>
 * Position of the dialog according to the trigger. Can be: auto, leftUp, leftBelow, rightBelow, rightUp.
 * <h4>disableClose: boolean</h4>
 * Whether the user can use escape or clicking outside to close a modal.
 * <h4>height: string</h4>
 * Height of the dialog.
 * <h4>width: string</h4>
 * Width of the dialog.
 * <h4>header: object</h4>
 * Dialog header object.
 * <h4>header.show: boolean</h4>
 * Whether to show dialog header section.
 * <h4>header.toolbar: object</h4>
 * Dialog header toolbar object.
 * <h4>content: object</h4>
 * Dialog content object.
 * <h4>content.show: boolean</h4>
 * Whether to show dialog content section.
 * <h4>content.message: string</h4>
 * Dialog message text.
 * <h4>content.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Template for the dialog content template section.
 * <h4>footer: object</h4>
 * Dialog footer object.
 * <h4>footer.show: boolean</h4>
 * Whether to show footer.
 * <h4>footer.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Template for the dialog footer template section.
 * <h4>footer.okButton: object</h4>
 * Footer ok button object.
 * <h4>footer.okButton.show: boolean</h4>
 * Whether to show footer ok button.
 * <h4>footer.okButton.button: ButtonModel</h4>
 * Ok button ButtonModel.
 * <h4>footer.cancelButton: object</h4>
 * Footer cancel button object.
 * <h4>footer.cancelButton.show: boolean</h4>
 * Whether to show footer cancel button.
 * <h4>footer.cancelButton.button: ButtonModel</h4>
 * Cancel button ButtonModel.
 * @type {?}
 */
CfDialogComponent.prototype.properties;
/**
 * Object for all styling properties:
 * <code><pre>
 * {{'{'}}
 *  header?: {{'{'}}                                 // Styling for the dialog header.
 *    class?: string;                          // Classes for header section.
 *    dynamicClass?: any;                      // Dynamic classes for header section.
 *    toolbar?: ToolbarStylingModel;           // ToolbarStylingModel for the header toolbar.
 *  {{'}'}};
 *  content?: {{'{'}}                                // Styling for the dialog content section with message and template inside.
 *    class?: string;                          // Classes for content section.
 *    dynamicClass?: any;                      // Dynamic classes for content section.
 *    message?: StylingModel;                  // StylingModel for the dialog content message.
 *    template?: StylingModel;                 // StylingModel for the dialog content template.
 *  {{'}'}};
 *  footer?: {{'{'}}                                 // Styling for the dialog footer.
 *    class?: string;                          // Classes for footer section.
 *    dynamicClass?: any;                      // Dynamic classes for footer section.
 *    okButton?: ButtonStylingModel;           // ButtonStylingModel for the dialog okButton.
 *    cancelButton?: ButtonStylingModel;       // ButtonStylingModel for the dialog cancelButton.
 *    template?: StylingModel;                 // StylingModel for the dialog template.
 *  {{'}'}};
 * {{'}'}}
 * </pre></code>
 * @type {?}
 */
CfDialogComponent.prototype.styling;
/**
 * @hidden
 * @type {?}
 */
CfDialogComponent.prototype.cfDialogTemplates;
/**
 * @hidden
 * @type {?}
 */
CfDialogComponent.prototype.elementRef;
/**
 * @hidden
 * @type {?}
 */
CfDialogComponent.prototype.dialogRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
